import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import agentData from '../../../../../../constants/agents/initialStates/agentData';
import { useNavigate } from 'react-router-dom';
import {
  setAgentData,
  setAgentEtc,
  setAIAgentData,
  setPrevAgentStep,
  setQualificationAgentData,
  setSmartReplyData,
} from '../../../../../../redux/slices/agents/agentSlice';
import { getAgentState } from '../../../../../../constants/agents/initialStates/agentState';
import { useCreateAIAgentMutation, useUpdateAIAgentMutation } from '../../../../../../services/api';
import useEventScope from '../../../../../events/controllers/useEventScope';
import { getEventContextPath } from '../../../../../events/controllers/eventFormatters';
import {
  withEventScopeForCreate,
  withEventScopeForUpdate,
} from '../../../../../events/controllers/eventScopePayload';

const useAIAgentHandler = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const agent = useSelector(state => state.agent);
  const brandingPrimaryColor = useSelector(state => state.branding.branding.primaryColor);
  const creationType = useSelector(state => state.agent.etc.creationType);
  const { isEventContext, eventId, workflowId, workflowKey } = useEventScope();

  const [_CREATE_AGENT, { isLoading: isCreating }] = useCreateAIAgentMutation();
  const [_UPDATE_AGENT, { isLoading: isUpdating }] = useUpdateAIAgentMutation();

  const onPrevHandler = () => {
    dispatch(setPrevAgentStep());
  };

  const onSubmitHandler = () => {
    switch (creationType) {
      case 'create':
        return createAgent();
      case 'edit':
        return editAgent();
      default:
        return createAgent();
    }
  };

  const createAgent = async () => {
    try {
      const payload = withEventScopeForCreate(agent.aiAgent, {
        isEventContext,
        eventId,
        workflowId,
      });
      const response = await _CREATE_AGENT(payload);

      if (response?.data?.success) {
        toast.success('Agent created');

        clearAgentData();
        navigate(
          isEventContext ? getEventContextPath(eventId, workflowKey) : agentData?.dashboardPath
        );
      }

      if (response?.error) {
        toast.error(
          response?.error?.data?.errorObject?.userErrorText || "Couldn't create the agent"
        );
      }
    } catch (error) {
      toast.error("Couldn't create the agent");
    }
  };

  const editAgent = async () => {
    try {
      const payload = withEventScopeForUpdate(
        agent.aiAgent,
        { isEventContext, eventId, workflowId },
        { eventId: agent.aiAgent?.eventId, workflowId: agent.aiAgent?.workflowId }
      );
      const response = await _UPDATE_AGENT(payload);

      if (response?.data?.success) {
        toast.success('Changes saved');

        clearAgentData();
        navigate(
          isEventContext ? getEventContextPath(eventId, workflowKey) : agentData?.dashboardPath
        );
      }

      if (response?.error) {
        toast.error(
          response?.error?.data?.errorObject?.userErrorText || "Couldn't save the changes"
        );
      }
    } catch (error) {
      toast.error("Couldn't save the changes");
    }
  };

  const clearAgentData = () => {
    const agentState = getAgentState(brandingPrimaryColor);
    dispatch(setAgentEtc({ ...agentState.etc, creationType: 'create' }));
    dispatch(setAgentData(agentState.data));
    dispatch(setAIAgentData(agentState.aiAgent));
    dispatch(setSmartReplyData(agentState.smartReply));
    dispatch(setQualificationAgentData(agentState.qualificationAgent));
  };

  return { onPrevHandler, onSubmitHandler, isCreating, isUpdating };
};

export default useAIAgentHandler;
